import { Bug } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import { BugReportsSettings } from '../components/settings/BugReportsSettings';
import { BugReportDetail } from '../components/bugReports/BugReportDetail';

/**
 * "Bug-uri raportate" — everything filed through the floating widget.
 *
 * A page of its own rather than a tab in Settings. It started as one, and the
 * tab strip there is twenty entries long and scrolls sideways, so the list
 * sat off the right edge and was effectively invisible — the admin who needed
 * it could not find it.
 *
 * ADMIN-only on both ends: the route is role-gated and every
 * `/api/bug-reports` endpoint is `authorizeRole('ADMIN')` server-side, so the
 * gate here is navigation, not the security border. Filing a bug stays open to
 * everyone through the widget.
 *
 * The open report (`?bug=…`) takes the whole surface, with no page heading and
 * no padding around it: the detail view brings its own breadcrumb and its own
 * properties sidebar, laid out exactly as a task's, and a second title bar
 * above that would push the sidebar out of alignment with the one on /tasks.
 */
export function BugReportsPage() {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('bug');

  // The open report lives in the URL so the link can be shared and the back
  // button does what it looks like it should.
  function selectBug(id: string | null) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (id) params.set('bug', id);
        else params.delete('bug');
        return params;
      },
      { replace: true },
    );
  }

  if (selectedId) {
    return <BugReportDetail id={selectedId} onBack={() => selectBug(null)} />;
  }

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      <div className="flex items-center gap-2">
        <Bug className="text-brand-600" size={24} />
        <h1 className="text-2xl font-bold text-gray-900">{t('settings.tab.bugReports')}</h1>
      </div>

      <div className="mt-6">
        <BugReportsSettings onOpen={(report) => selectBug(report.id)} />
      </div>
    </div>
  );
}
